// Backend API base URL - update this to match your Django backend URL
import axios from "axios";
import { 
  getAuthToken, 
  setAuthToken, 
  setRefreshToken, 
  setAuthUser, 
  getRefreshToken,
  clearAuthCookies, 
  setUserinfo
} from './cookie-utils';
import Cookies from "js-cookie";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

console.log("Backend API Base URL:", API_BASE_URL);

// Create axios instance for backend API
const backendApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token (except for login and register endpoints)
backendApi.interceptors.request.use(
  (config) => {
    // List of endpoints that don't need authentication
    const publicEndpoints = [
      '/company/login/',
      '/company/register-company/',
      '/auth/login/',
      '/auth/register/',
      '/auth/login/refresh/'
    ];
    
    // Check if current request is to a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    // Only add token if it's not a public endpoint
    if (!isPublicEndpoint) {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Handle FormData - remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
backendApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // List of endpoints that don't need token refresh
    const publicEndpoints = [
      '/company/login/',
      '/company/register-company/',
      '/auth/login/',
      '/auth/register/',
      '/auth/login/refresh/'
    ];
    
    // Check if current request is to a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      originalRequest.url?.includes(endpoint)
    );
    
    // Only attempt token refresh for protected endpoints
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      originalRequest._retry = true;
      
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/login/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          setAuthToken(access);
          
          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return backendApi(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          clearAuthCookies();
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Company Authentication API calls
export const postCompanyLogin = async (loginData) => {
  try {
    const response = await backendApi.post("/company/login/", {
      email: loginData.email,
      domain: loginData.company_code,
      password: loginData.password,
    });
    
    if (response.data.status_code === 200) {
      // Store tokens and user data in cookies
      const { access, refresh, user } = response.data.data;
      setAuthToken(access);
      setRefreshToken(refresh);
      setAuthUser(user);
      userInfo(access); // Fetch and store additional user info if needed
      return {
        success: true,
        user: user,
        access_token: access,
        refresh_token: refresh,
      };
    } else {
      throw new Error(response.data.message || "Login failed");
    }
  } catch (error) {
    console.error("Login API Error:", error);
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error("Login failed. Please try again.");
    }
  }
};
const userInfo = async (token) => {
  try {
    const response = await backendApi.get("/auth/user/info/", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log(response, 'response');
    // Cookies.set("user_info", JSON.stringify(response?.data?.data), {
    //   expires: 1,
    //   secure: true,
    //   sameSite: "Strict",
    // });
setUserinfo(response?.data?.data);
  } catch (error) {
    console.error("User Info API Error:", error);
    throw error;
  }
};
// Company Registration API call
export const postCompanyRegister = async (registerData) => {
  try {
    const response = await backendApi.post("/company/register-company/", registerData);
    
    if (response.data.status_code === 201) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } else {
      throw new Error(response.data.message || "Registration failed");
    }
  } catch (error) {
    console.error("Registration API Error:", error);
    
    if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error("Registration failed. Please try again.");
    }
  }
};

// Logout function
export const postLogout = async () => {
  try {
    // Clear cookies
    clearAuthCookies();
    
    return { success: true };
  } catch (error) {
    console.error("Logout Error:", error);
    return { success: false };
  }
};

// Get current user profile
export const getCurrentUserProfile = async () => {
  try {
    const response = await backendApi.get("/auth/user/profile/");
    return response.data;
  } catch (error) {
    console.error("Get Profile Error:", error);
    throw error;
  }
};

// Generic API methods for other endpoints
export const get = async (url, config = {}) => {
  try {
    const response = await backendApi.get(url, config);
    return response.data;
  } catch (error) {
    console.error(`GET ${url} Error:`, error);
    throw error;
  }
};

export const post = async (url, data, config = {}) => {
  try {
    const response = await backendApi.post(url, data, config);
    return response.data;
  } catch (error) {
    console.error(`POST ${url} Error:`, error);
    throw error;
  }
};

export const put = async (url, data, config = {}) => {
  try {
    const response = await backendApi.put(url, data, config);
    return response.data;
  } catch (error) {
    console.error(`PUT ${url} Error:`, error);
    throw error;
  }
};

export const del = async (url, config = {}) => {
  try {
    const response = await backendApi.delete(url, config);
    return response.data;
  } catch (error) {
    console.error(`DELETE ${url} Error:`, error);
    throw error;
  }
};

// ============================================================================
// COMPANY MANAGEMENT APIs
// ============================================================================

// Company APIs
export const getCompanies = async (params = {}) => {
  try {
  // Default: fetch only non-archived companies unless caller overrides
  // const mergedParams = { is_archived: false, ...params };
  const response = await backendApi.get("/company/companies/", { params });
  return response.data;
  } catch (error) {
    console.error("Get Companies Error:", error);
    console.error("Error details:", error.response?.data);
    console.error("Error status:", error.response?.status);
    throw error;
  }
};

export const getCompany = async (id) => {
  try {
    const response = await backendApi.get(`/company/companies/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Get Company Error:", error);
    throw error;
  }
};

export const createCompany = async (companyData) => {
  try {
    // Ensure newly created companies are not archived by default.
    const payload = { is_archived: false, ...companyData };
    console.log("createCompany payload:", payload);
    const response = await backendApi.post("/company/companies/", payload);
    console.log("createCompany raw response:", response);
    console.log("createCompany response.data:", response.data);
    console.log("createCompany response.data keys:", response.data ? Object.keys(response.data) : "No response.data");
    return response.data;
  } catch (error) {
    console.error("Create Company Error:", error);
    throw error;
  }
};

export const updateCompany = async (id, companyData) => {
  try {
    const response = await backendApi.put(`/company/companies/${id}/`, companyData);
    return response.data;
  } catch (error) {
    console.error("Update Company Error:", error);
    throw error;
  }
};

export const deleteCompany = async (id) => {
  try {
    const response = await backendApi.delete(`/company/companies/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Company Error:", error);
    throw error;
  }
};

// Contact APIs
export const getContacts = async (params = {}) => {
  try {
    const response = await backendApi.get("/company/contacts/", { params });
    return response.data;
  } catch (error) {
    console.error("Get Contacts Error:", error);
    throw error;
  }
};

export const getContact = async (id) => {
  try {
    const response = await backendApi.get(`/company/contacts/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Get Contact Error:", error);
    throw error;
  }
};

export const createContact = async (contactData) => {
  try {
    const response = await backendApi.post("/company/contacts/", contactData);
    return response.data;
  } catch (error) {
    console.error("Create Contact Error:", error);
    throw error;
  }
};

export const updateContact = async (id, contactData) => {
  try {
    const response = await backendApi.put(`/company/contacts/${id}/`, contactData);
    return response.data;
  } catch (error) {
    console.error("Update Contact Error:", error);
    throw error;
  }
};

export const deleteContact = async (id) => {
  try {
    const response = await backendApi.delete(`/company/contacts/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Contact Error:", error);
    throw error;
  }
};

// Employee APIs
export const getEmployees = async (params = {}) => {
  try {
    const response = await backendApi.get("/company/employees/", { params });
    return response.data;
  } catch (error) {
    console.error("Get Employees Error:", error);
    throw error;
  }
};

export const getEmployee = async (params) => {
  try {
    const response = await backendApi.get(`/company/employees/`,  params );
    return response.data;
  } catch (error) {
    console.error("Get Employee Error:", error);
    throw error;
  }
};

export const createEmployee = async (employeeData) => {
  try {
    const response = await backendApi.post("/company/employees/", employeeData);
    return response.data;
  } catch (error) {
    console.error("Create Employee Error:", error);
    throw error;
  }
};

export const updateEmployee = async (id, employeeData) => {
  try {
    const response = await backendApi.put(`/company/employees/${id}/`, employeeData);
    return response.data;
  } catch (error) {
    console.error("Update Employee Error:", error);
    throw error;
  }
};

export const deleteEmployee = async (id) => {
  try {
    const response = await backendApi.delete(`/company/employees/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Employee Error:", error);
    throw error;
  }
};

// Project APIs
export const getProjects = async (params = {}) => {
  try {
    const response = await backendApi.get("/company/projects/", { params });
    return response.data;
  } catch (error) {
    console.error("Get Projects Error:", error);
    throw error;
  }
};

export const getProject = async (params) => {
  try {
    const response = await backendApi.get(`/company/projects/`, {
      params,   // company_id, is_archived, etc.
    });
    return response.data;
  } catch (error) {
    console.error("Get Project Error:", error);
    throw error;
  }
};


export const createProject = async (projectData) => {
  try {
    const response = await backendApi.post("/company/projects/", projectData);
    return response.data;
  } catch (error) {
    console.error("Create Project Error:", error);
    throw error;
  }
};

export const updateProject = async (id, projectData) => {
  try {
    const response = await backendApi.put(`/company/projects/${id}/`, projectData);
    return response.data;
  } catch (error) {
    console.error("Update Project Error:", error);
    throw error;
  }
};

export const deleteProject = async (id) => {
  try {
    const response = await backendApi.delete(`/company/projects/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Project Error:", error);
    throw error;
  }
};

// WorkType APIs
export const getWorkTypes = async (params = {}) => {
  try {
    const response = await backendApi.get("/company/worktypes/", { params });
    return response.data;
  } catch (error) {
    console.error("Get WorkTypes Error:", error);
    throw error;
  }
};

export const getWorkType = async (id) => {
  try {
    const response = await backendApi.get(`/company/worktypes/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Get WorkType Error:", error);
    throw error;
  }
};

export const createWorkType = async (workTypeData) => {
  try {
    const response = await backendApi.post("/company/worktypes/", workTypeData);
    return response.data;
  } catch (error) {
    console.error("Create WorkType Error:", error);
    throw error;
  }
};

export const updateWorkType = async (id, workTypeData) => {
  try {
    const response = await backendApi.put(`/company/worktypes/${id}/`, workTypeData);
    return response.data;
  } catch (error) {
    console.error("Update WorkType Error:", error);
    throw error;
  }
};

export const deleteWorkType = async (id) => {
  try {
    const response = await backendApi.delete(`/company/worktypes/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Delete WorkType Error:", error);
    throw error;
  }
};

// Lookup APIs
export const getLookups = async (params = {}) => {
  try {
    const response = await backendApi.get("/company/lookups/", { params });
    return response.data;
  } catch (error) {
    console.error("Get Lookups Error:", error);
    throw error;
  }
};

export const getLookupsByName = async (name) => {
  try {
    const response = await backendApi.get(`/company/lookups/?name=${name}`);
    return response.data;
  } catch (error) {
    console.error("Get Lookups By Name Error:", error);
    throw error;
  }
};

// Private Users API
export const getPrivateUsers = async () => {
  try {
    const response = await backendApi.get("/company/private-users/");
    return response.data;
  } catch (error) {
    console.error("Get Private Users Error:", error);
    throw error;
  }
};

// Project Phases APIs
export const getProjectPhases = async (params = {}) => {
  try {
    console.log('getProjectPhases called with params:', params);
    const response = await backendApi.get("/company/project-phases/", { params });
    console.log('getProjectPhases response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Get Project Phases Error:", error);
    console.error("Get Project Phases Error details:", error.response?.data);
    throw error;
  }
};

export const getProjectPhasesByProjectId = async (projectId) => {
  try {
    console.log('getProjectPhasesByProjectId called with projectId:', projectId);
    // Try different URL patterns that might be expected by the backend
    let response;
    try {
      // First try: /company/project-phases/{projectId}/
      response = await backendApi.get(`/company/project-phases/${projectId}/`);
      console.log('getProjectPhasesByProjectId response (pattern 1):', response.data);
    } catch (error) {
      console.log('Pattern 1 failed, trying pattern 2:', error.response?.status);
      try {
        // Second try: /company/projects/{projectId}/phases/
        response = await backendApi.get(`/company/projects/${projectId}/phases/`);
        console.log('getProjectPhasesByProjectId response (pattern 2):', response.data);
      } catch (error2) {
        console.log('Pattern 2 failed, trying pattern 3:', error2.response?.status);
        // Third try: /company/project/{projectId}/phases/
        response = await backendApi.get(`/company/project/${projectId}/phases/`);
        console.log('getProjectPhasesByProjectId response (pattern 3):', response.data);
      }
    }
    return response.data;
  } catch (error) {
    console.error("Get Project Phases By Project ID Error:", error);
    console.error("Get Project Phases By Project ID Error details:", error.response?.data);
    throw error;
  }
};

export const createProjectPhase = async (phaseData) => {
  try {
    const response = await backendApi.post("/company/project-phases/", phaseData);
    return response.data;
  } catch (error) {
    console.error("Create Project Phase Error:", error);
    throw error;
  }
};

export const updateProjectPhase = async (phaseId, phaseData) => {
  try {
    const response = await backendApi.put(`/company/project-phases/${phaseId}/`, phaseData);
    return response.data;
  } catch (error) {
    console.error("Update Project Phase Error:", error);
    throw error;
  }
};

export const deleteProjectPhase = async (phaseId) => {
  try {
    console.log('deleteProjectPhase called with ID:', phaseId);
    
    // Try multiple delete endpoint patterns
    let response;
    try {
      // First try: Standard REST pattern
      response = await backendApi.delete(`/company/project-phases/${phaseId}/`);
      console.log('deleteProjectPhase response (pattern 1):', response);
    } catch (error) {
      console.log('Delete pattern 1 failed, trying pattern 2:', error.response?.status);
      try {
        // Second try: Alternative pattern
        response = await backendApi.delete(`/company/project-phase/${phaseId}/`);
        console.log('deleteProjectPhase response (pattern 2):', response);
      } catch (error2) {
        console.log('Delete pattern 2 failed, trying pattern 3:', error2.response?.status);
        // Third try: Different structure
        response = await backendApi.delete(`/company/phases/${phaseId}/`);
        console.log('deleteProjectPhase response (pattern 3):', response);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Delete Project Phase Error:", error);
    console.error("Delete Project Phase Error details:", error.response?.data);
    console.error("Delete Project Phase Error status:", error.response?.status);
    throw error;
  }
};

// Project Delay APIs
export const getProjectDelays = async (params = {}) => {
  try {
    console.log('getProjectDelays called with params:', params);
    const response = await backendApi.get("/company/project-delays/", { params });
    console.log('getProjectDelays response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Get Project Delays Error:", error);
    console.error("Get Project Delays Error details:", error.response?.data);
    throw error;
  }
};

export const getProjectDelaysByProjectId = async (projectId) => {
  try {
    console.log('getProjectDelaysByProjectId called with projectId:', projectId);
    
    // Try multiple endpoint patterns
    let response;
    try {
      // First try: /company/project-delays/{projectId}/
      response = await backendApi.get(`/company/project-delays/${projectId}/`);
      console.log('getProjectDelaysByProjectId response (pattern 1):', response.data);
    } catch (error) {
      console.log('Pattern 1 failed, trying pattern 2:', error.response?.status);
      try {
        // Second try: /company/projects/{projectId}/delays/
        response = await backendApi.get(`/company/projects/${projectId}/delays/`);
        console.log('getProjectDelaysByProjectId response (pattern 2):', response.data);
      } catch (error2) {
        console.log('Pattern 2 failed, trying pattern 3:', error2.response?.status);
        // Third try: /company/project/{projectId}/delays/
        response = await backendApi.get(`/company/project/${projectId}/delays/`);
        console.log('getProjectDelaysByProjectId response (pattern 3):', response.data);
      }
    }
    return response.data;
  } catch (error) {
    console.error("Get Project Delays By Project ID Error:", error);
    console.error("Get Project Delays By Project ID Error details:", error.response?.data);
    throw error;
  }
};

export const createProjectDelay = async (delayData) => {
  try {
    console.log('createProjectDelay called with data:', delayData);
    const response = await backendApi.post("/company/project-delays/", delayData);
    console.log('createProjectDelay response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Create Project Delay Error:", error);
    console.error("Create Project Delay Error details:", error.response?.data);
    throw error;
  }
};

export const updateProjectDelay = async (delayId, delayData) => {
  try {
    console.log('updateProjectDelay called with ID:', delayId, 'and data:', delayData);
    const response = await backendApi.put(`/company/project-delays/${delayId}/`, delayData);
    console.log('updateProjectDelay response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Update Project Delay Error:", error);
    console.error("Update Project Delay Error details:", error.response?.data);
    throw error;
  }
};

export const deleteProjectDelay = async (delayId) => {
  try {
    console.log('deleteProjectDelay called with ID:', delayId);
    
    // Try multiple delete endpoint patterns
    let response;
    try {
      // First try: Standard REST pattern
      response = await backendApi.delete(`/company/project-delays/${delayId}/`);
      console.log('deleteProjectDelay response (pattern 1):', response);
    } catch (error) {
      console.log('Delete pattern 1 failed, trying pattern 2:', error.response?.status);
      try {
        // Second try: Alternative pattern
        response = await backendApi.delete(`/company/project-delay/${delayId}/`);
        console.log('deleteProjectDelay response (pattern 2):', response);
      } catch (error2) {
        console.log('Delete pattern 2 failed, trying pattern 3:', error2.response?.status);
        // Third try: Different structure
        response = await backendApi.delete(`/company/delays/${delayId}/`);
        console.log('deleteProjectDelay response (pattern 3):', response);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Delete Project Delay Error:", error);
    console.error("Delete Project Delay Error details:", error.response?.data);
    console.error("Delete Project Delay Error status:", error.response?.status);
    throw error;
  }
};

// Document Management APIs
export const getEntityDocuments = async (entityType, entityId) => {
  try {
    const response = await backendApi.get(`/company/${entityType}/${entityId}/documents/`);
    return response.data;
  } catch (error) {
    console.error("Get Entity Documents Error:", error);
    throw error;
  }
};

export const createEntityDocument = async (entityType, entityId, documentData) => {
  try {
    console.log("createEntityDocument called with:", { entityType, entityId });
    console.log("Document data type:", documentData instanceof FormData ? "FormData" : typeof documentData);
    
    // Log FormData contents if it's FormData
    if (documentData instanceof FormData) {
      console.log("FormData contents:");
      for (let [key, value] of documentData.entries()) {
        if (value instanceof File) {
          console.log(key + ':', `File: ${value.name} (${value.size} bytes, type: ${value.type})`);
        } else if (value instanceof Blob) {
          console.log(key + ':', `Blob: (${value.size} bytes, type: ${value.type})`);
        } else {
          console.log(key + ':', value);
        }
      }
    }
    
    const response = await backendApi.post(`/company/${entityType}/${entityId}/documents/`, documentData);
    
    console.log("createEntityDocument response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Create Entity Document Error:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error status:", error.response?.status);
    console.error("Validation errors:", error.response?.data?.errors);
    console.error("Full error details:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers
    });
    throw error;
  }
};

export const updateEntityDocument = async (entityType, entityId, documentId, documentData) => {
  try {
    console.log("updateEntityDocument called with:", { entityType, entityId, documentId });
    
    const response = await backendApi.put(`/company/${entityType}/${entityId}/documents/${documentId}/`, documentData);
    
    console.log("updateEntityDocument response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Update Entity Document Error:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

export const deleteEntityDocument = async (entityType, entityId, documentId) => {
  try {
    console.log("deleteEntityDocument called with:", { entityType, entityId, documentId });
    
    const response = await backendApi.delete(`/company/${entityType}/${entityId}/documents/${documentId}/`);
    
    console.log("deleteEntityDocument response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Delete Entity Document Error:", error);
    console.error("Error response:", error.response?.data);
    throw error;
  }
};

// Notes Management APIs
export const getEntityNotes = async (entityType, entityId) => {
  try {
    const response = await backendApi.get(`/company/${entityType}/${entityId}/notes/`);
    return response.data;
  } catch (error) {
    console.error("Get Entity Notes Error:", error);
    throw error;
  }
};

export const createEntityNote = async (entityType, entityId, noteData) => {
  try {
    const response = await backendApi.post(`/company/${entityType}/${entityId}/notes/`, noteData);
    return response.data;
  } catch (error) {
    console.error("Create Entity Note Error:", error);
    throw error;
  }
};

export const updateEntityNote = async (entityType, entityId, noteId, noteData) => {
  try {
    const response = await backendApi.put(`/company/${entityType}/${entityId}/notes/${noteId}/`, noteData);
    return response.data;
  } catch (error) {
    console.error("Update Entity Note Error:", error);
    throw error;
  }
};

export const deleteEntityNote = async (entityType, entityId, noteId) => {
  try {
    const response = await backendApi.delete(`/company/${entityType}/${entityId}/notes/${noteId}/`);
    return response.data;
  } catch (error) {
    console.error("Delete Entity Note Error:", error);
    throw error;
  }
};

// Activities Management APIs
export const getEntityActivities = async (entityType, entityId) => {
  try {
    const response = await backendApi.get(`/company/${entityType}/${entityId}/activities/`);
    return response.data;
  } catch (error) {
    console.error("Get Entity Activities Error:", error);
    throw error;
  }
};

export const createEntityActivity = async (entityType, entityId, activityData) => {
  try {
    const response = await backendApi.post(`/company/${entityType}/${entityId}/activities/`, activityData);
    return response.data;
  } catch (error) {
    console.error("Create Entity Activity Error:", error);
    throw error;
  }
};

export default backendApi;
